// Legacy qualitative bands are kept only for semantic color/tone mapping.
// The user-facing score is rendered separately on a 1–10 scale.
const GRADE_BANDS = [
  [95, "S"],
  [90, "S-"],
  [85, "A+"],
  [80, "A"],
  [75, "A-"],
  [70, "B+"],
  [65, "B"],
  [60, "B-"],
  [55, "C+"],
  [45, "C"],
  [20, "C-"],
  [0, "D"],
];

// Churn Risk keeps its raw 0–100 bar direction: a longer bar means more risk.
// Only its semantic tone is inverted, so the existing green→yellow→red color
// calibration stays unchanged while the displayed rating remains high = high risk.
const CHURN_RISK_BANDS = [
  [9, "S"],
  [19, "S-"],
  [29, "A+"],
  [34, "A"],
  [39, "A-"],
  [49, "B+"],
  [59, "B"],
  [74, "B-"],
  [79, "C+"],
  [84, "C"],
  [89, "C-"],
  [100, "D"],
];

const GRADE_TONE = {
  S: "excellent",
  "S-": "excellent",
  "A+": "good",
  A: "good",
  "A-": "bridge",
  "B+": "bridge",
  B: "caution",
  "B-": "caution",
  "C+": "warning",
  C: "warning",
  "C-": "warning",
  D: "danger",
};

export function isNumericScore(value) {
  if (typeof value === "number") return Number.isFinite(value);
  if (typeof value !== "string" || value.trim() === "") return false;
  return Number.isFinite(Number(value));
}

function normalizedScore(value, fallback = null) {
  if (!isNumericScore(value)) return fallback;
  return Math.max(0, Math.min(100, Number(value)));
}

export function interpretedScore(value, { invert = false, fallback = -Infinity } = {}) {
  const normalized = normalizedScore(value, null);
  if (normalized == null) return fallback;
  return invert ? 100 - normalized : normalized;
}

// Presentation-only 1–10 rating. Raw scores remain untouched in storage/API.
// 50–53 => 5-, 54–56 => 5, 57–59 => 5+, 60–63 => 6-, etc.
// The floor is 1- and a perfect raw 100 is the only value shown as 10.
export function scoreToRating(value, { fallback = "—" } = {}) {
  const normalized = normalizedScore(value, null);
  if (normalized == null) return fallback;
  if (normalized >= 100) return "10";
  if (normalized < 14) return "1-";
  if (normalized < 17) return "1";
  if (normalized < 20) return "1+";

  const whole = Math.floor(normalized / 10);
  const remainder = normalized - (whole * 10);
  if (remainder < 4) return `${whole}-`;
  if (remainder < 7) return `${whole}`;
  return `${whole}+`;
}

// Kept for semantic tone compatibility and tests. UI rendering should use
// scoreToRating() so the old D–S scale never leaks into the product surface.
export function scoreToGrade(value, { invert = false, fallback = "—" } = {}) {
  const normalized = normalizedScore(value, null);
  if (normalized == null) return fallback;

  if (invert) {
    return CHURN_RISK_BANDS.find(([maximum]) => normalized <= maximum)?.[1] || fallback;
  }

  return GRADE_BANDS.find(([minimum]) => normalized >= minimum)?.[1] || fallback;
}

export function gradeToTone(grade, fallback = "danger") {
  return GRADE_TONE[grade] || fallback;
}

export function scoreToTone(value, options = {}) {
  const fallback = options.fallback || "danger";
  const normalized = normalizedScore(value, null);
  if (normalized == null) return fallback;

  // Presentation-only transition between warning and caution. Keep the legacy
  // semantic grades unchanged, but give non-inverted raw scores 50–59 their own
  // amber tone so the 5-range reads as an intermediate step rather than full warning.
  // Inverted metrics (for example Churn Risk) keep their existing calibration.
  if (!options.invert && normalized >= 50 && normalized < 60) return "amber";

  const grade = scoreToGrade(normalized, { ...options, fallback: null });
  return grade ? gradeToTone(grade, fallback) : fallback;
}

export function scoreValueToRating(value, options = {}) {
  if (value == null || value === "") return options.fallback || "Not established";
  return isNumericScore(value) ? scoreToRating(value, options) : String(value);
}

// Legacy helper retained for compatibility with any non-UI code that still
// expects the old grade representation.
export function scoreValueToGrade(value, options = {}) {
  if (value == null || value === "") return options.fallback || "Not established";
  return isNumericScore(value) ? scoreToGrade(value, options) : String(value);
}

// Signal Profile and Skills are always ordered by the raw numeric value that
// controls the physical bar length. Churn Risk keeps its inverse semantic tone,
// but its position still follows the same highest-to-lowest raw-score rule.
export function sortScoreMetrics(metrics = []) {
  return metrics
    .map((metric, index) => ({ ...metric, __scoreOrder: index }))
    .sort((left, right) => {
      const rightScore = normalizedScore(right.value, -Infinity);
      const leftScore = normalizedScore(left.value, -Infinity);
      return rightScore - leftScore || left.__scoreOrder - right.__scoreOrder;
    })
    .map(({ __scoreOrder, ...metric }) => metric);
}
